import React, { useCallback, useContext, useState } from 'react';
import { Alert, Box, Button, SpaceBetween } from '@amzn/awsui-components-react';
import { useNavigate } from 'react-router-dom';
import { AppConfigContext } from '../../config/appConfigContext';
import { isNetradyneDissociationEnabled } from '../../constants/featureFlags';
import { OnboardingStatus, fromValue, toStatusLabel } from '../../constants/onboardingStatus';
import { ComponentOnboardingStatus } from '../../services/getDeviceComponentStatusHandler';
import ComponentInstallationDetails from './componentInstallationDetails';
import EmeraldInformationTable from './emeraldInformationTable';
import NetradyneDissociation from './netradyneDissociation';
import { ComponentType } from './componentType';

interface InstallationSuccessProps {
    deviceId: string;
    vin: string;
    componentType: ComponentType;
    serialNumber: string;
    notes?: string;
    existingComponentInfo?: ComponentOnboardingStatus;
    emeraldInformation?: any;
    onGoHome?: () => void;
}

const InstallationSuccess: React.FC<InstallationSuccessProps> = ({
    deviceId,
    vin,
    componentType,
    serialNumber,
    notes,
    existingComponentInfo,
    emeraldInformation,
    onGoHome,
}) => {
    const navigate = useNavigate();
    const { config } = useContext(AppConfigContext);

    const handleGoHome = useCallback(() => {
        if (onGoHome) {
            onGoHome();
        } else {
            navigate('/');
        }
    }, [navigate, onGoHome]);

    const isExistingInstallation = !!existingComponentInfo;
    const componentId = existingComponentInfo?.componentId || serialNumber;

    // Seeded from the backend status so a refresh after the request resumes on the outcome. The status
    // arrives as the backend enum name, so it has to go through fromValue before it can be compared.
    const [dissociationRequested, setDissociationRequested] = useState(
        fromValue(existingComponentInfo?.status) === OnboardingStatus.READY_FOR_NETRADYNE_DISSOCIATION,
    );
    const showDissociation = isNetradyneDissociationEnabled(config);

    const renderSuccessAlert = () => {
        if (isExistingInstallation) {
            return (
                <Alert type="success" header="Component Already Installed">
                    <SpaceBetween size="xs">
                        <div>This device already has a {componentType.label} component successfully installed.</div>
                        <div>You cannot install another component of the same type on this device.</div>
                    </SpaceBetween>
                </Alert>
            );
        }

        return (
            <Alert type="success" header="Installation Completed Successfully">
                <SpaceBetween size="xs">
                    <div>
                        The {componentType.label} component has been successfully installed and the device health check
                        has passed.
                    </div>
                    <div>The device is now ready for operation with the new {componentType.label} component.</div>
                </SpaceBetween>
            </Alert>
        );
    };

    return (
        <SpaceBetween size="l">
            <Box variant="h2">
                {isExistingInstallation ? 'Component Already Installed' : 'Installation Completed Successfully'}
            </Box>

            {renderSuccessAlert()}

            <ComponentInstallationDetails
                deviceId={deviceId}
                vin={vin}
                componentType={componentType}
                serialNumber={componentId}
                status={toStatusLabel(
                    isExistingInstallation ? fromValue(existingComponentInfo?.status) : OnboardingStatus.COMPLETED,
                )}
                notes={notes || ''}
                title="Component Details"
            />

            {emeraldInformation && <EmeraldInformationTable emeraldInformation={emeraldInformation} />}

            {showDissociation && (
                <NetradyneDissociation
                    deviceId={deviceId}
                    vin={vin}
                    componentType={componentType}
                    serialNumber={componentId}
                    alreadyRequested={dissociationRequested}
                    onRequested={() => setDissociationRequested(true)}
                />
            )}

            <SpaceBetween size="s" direction="horizontal">
                <Button
                    variant={showDissociation && !dissociationRequested ? 'normal' : 'primary'}
                    onClick={handleGoHome}
                >
                    Return to Home
                </Button>
            </SpaceBetween>
        </SpaceBetween>
    );
};

export default InstallationSuccess;
